import SelectField from "../select-field/select-field.component";
import FormField from "../form-field/form-field.component";
import CreditCardPreview from "../credit-card-preview/credit-card-preview.component";

import { useEffect, useState, useContext } from "react";
import { Stack, Box } from "@mui/material";
import { UserContext } from "../../context/user.context";
import { getBannerOptions } from "../../service/order.service";
import { maxInstallments } from '../../config/installments';

const CreditCardForm = ({
  creditCardInfo,
  value,
  onChange,
  errors,
  insuranceCompany,
}) => {
  const [installmentsOptions, setInstallmentsOptions] = useState([]);
  const { banner, number, fullName, month, year, cvv, installments, cpf } =
    creditCardInfo;
  const [bannerOptions, setBannerOptions] = useState([]);
  const [bannerLoading, setBannerLoading] = useState(false);
  const { authUser } = useContext(UserContext);

  // TODO improve SelectField component so it send just the installments value
  // to the backend, not the hole label
  useEffect(() => {
    const newInstallments = [`1x R$ ${formatPrice(value)}`];

    for (let i = 2; i <= maxInstallments(value); i++) {
      newInstallments.push(`${i}x R$ ${formatPrice(value / i)}`);
    }

    setInstallmentsOptions(newInstallments);
  }, [value]);

  useEffect(() => {
    async function fetchBannerOptions() {
      if (!authUser || !insuranceCompany) return;

      setBannerLoading(true);
      try {
        const token = await authUser.getIdToken();
        const options = await getBannerOptions(token, insuranceCompany);
        setBannerOptions(options);
      } catch (error) {
        console.error("Failed to fetch banner options:", error);
        // Set default options as fallback
        setBannerOptions([
          "Amex",
          "Aura",
          "Diners",
          "Discover",
          "Elo",
          "Hiper",
          "Hipercard",
          "JCB",
          "MasterCard",
          "Visa",
        ]);
      } finally {
        setBannerLoading(false);
      }
    }

    fetchBannerOptions();
  }, [authUser, insuranceCompany]);

  const formatPrice = (value) => {
    return value.toFixed(2).toLocaleString("pt-BR").replace(".", ",");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    onChange({ ...creditCardInfo, [name]: value });
  };

  const getErrorMessage = (fieldName) => {
    return !!errors ? errors[fieldName] : null;
  };

  const cvvMask =
    banner &&
    ["amex", "american express"].some((b) => banner.toLowerCase().includes(b))
      ? "9999"
      : "999";

  const cardNumberMask =
    banner &&
    ["amex", "american express"].some((b) => banner.toLowerCase().includes(b))
      ? "9999 999999 99999"
      : "9999 9999 9999 9999";

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
      <Stack direction={"column"} spacing={4}>
        <SelectField
          type="text"
          label="Bandeira do cartão"
          name="banner"
          variant="light grey"
          autocomplete="off"
          required
          onChange={handleChange}
          value={banner}
          options={bannerOptions}
          disabled={bannerLoading}
        />
        <FormField
          type="text"
          label="Número do cartão"
          name="number"
          variant="light"
          mask={cardNumberMask}
          required
          onChange={handleChange}
          value={number}
          errorMessage={getErrorMessage("number")}
        />
        <FormField
          type="text"
          label="Nome do titular (impresso no cartão)"
          name="fullName"
          mask={"*".repeat(50)}
          variant="light"
          required
          onChange={handleChange}
          value={fullName}
          errorMessage={getErrorMessage("fullName")}
        />
        <FormField
          label="CPF"
          name="cpf"
          variant="light"
          required
          onChange={handleChange}
          value={cpf}
          mask="999.999.999-99"
          errorMessage={getErrorMessage("cpf")}
        />
        <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
          <FormField
            type="text"
            label="Mês"
            name="month"
            variant="light"
            mask="99"
            maskChar="M"
            required
            onChange={handleChange}
            value={month}
            errorMessage={getErrorMessage("month")}
          />
          <FormField
            type="text"
            label="Ano"
            name="year"
            variant="light"
            mask="9999"
            maskChar="A"
            required
            onChange={handleChange}
            value={year}
            errorMessage={getErrorMessage("year")}
          />
          <FormField
            type="text"
            label="CVV"
            name="cvv"
            variant="light"
            mask={cvvMask}
            required
            onChange={handleChange}
            value={cvv}
            errorMessage={getErrorMessage("cvv")}
          />
        </Stack>
        <SelectField
          type="text"
          label="Selecione o número de parcelas"
          name="installments"
          variant="light grey"
          autocomplete="off"
          required
          onChange={handleChange}
          value={installments}
          options={installmentsOptions}
        />
      </Stack>
      <Box
        className="preview-container"
        sx={{
          display: { xs: "none", sm: "block" },
        }}
      >
        <CreditCardPreview creditCardInfo={creditCardInfo} />
      </Box>
    </Stack>
  );
};

export default CreditCardForm;
